import { installWebGPUStagingRecovery } from './webgpuStagingRecovery';
import type { WebgpuGraphicsDevice } from 'playcanvas';

type RecoverableDevice = {
    createDevice(): ReturnType<WebgpuGraphicsDevice['createDevice']>;
    contextLost?: boolean;
    destroy(): void;
    loseContext(): void;
    fire(event: string): void;
};

/** Install our recovery ownership before PlayCanvas binds its lost callback.
 * A late adapter/device result is destroyed after the initialization deadline. */
export async function initializeWebGPUDevice<T extends RecoverableDevice>(device: T): Promise<T> {
    (device as T & { handleDeviceLost: (info: { reason: string }) => void }).handleDeviceLost = info => {
        if (info.reason !== 'destroyed') {
            device.loseContext();
            device.fire('devicelost');
        }
    };
    let abandoned = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const destroy = () => { try { device.destroy(); } catch { /* partial initialization */ } };
    const pending = device.createDevice();
    void pending.then(() => { if (abandoned) destroy(); }, () => { if (abandoned) destroy(); });
    try {
        const initialized = await Promise.race([
            pending,
            new Promise<never>((_, reject) => {
                timer = setTimeout(() => reject(new Error('WebGPU initialization timed out')), 20000);
            }),
        ]);
        if (!initialized) throw new Error('No usable WebGPU adapter');
        if (device.contextLost) throw new Error('WebGPU device was lost during initialization');
        installWebGPUStagingRecovery(device);
        return device;
    } catch (error) {
        abandoned = true;
        destroy();
        throw error;
    } finally { clearTimeout(timer); }
}
